import { useMemo } from 'react';
import { keys } from 'remeda';
import {
  ConditionType,
  DataEntityBase,
  SelectorValue,
  useSelectedPartner,
} from 'src/graphql';
import { useSignalRulesConfig } from 'src/graphql/entities/signal-oncall-schedule/hooks/signal-rules-config';
import {
  mapCondition,
  OperatorsMap,
  UseQueryOptions,
  useQueryOptions,
} from 'src/pages/Settings/rules/use-rules-engine';

export interface SignalRulesEngineConfig {
  conditions: SelectorValue[];
  timezone?: string;
  queryOptions?: UseQueryOptions;
}

const DEFAULT_CONFIG: SignalRulesEngineConfig = {
  conditions: [],
  timezone: undefined,
};

export function useSignalRulesEngine(): SignalRulesEngineConfig {
  const { selectedPartner } = useSelectedPartner();

  const { config: signalRulesConfig } = useSignalRulesConfig();
  const queryOptions = useQueryOptions(signalRulesConfig);

  const operators = useMemo<OperatorsMap>(() => {
    if (!signalRulesConfig) return {} as OperatorsMap;

    const configOperators = signalRulesConfig.operators;

    return keys.strict(configOperators).reduce((acc, operatorType) => {
      return {
        ...acc,
        [operatorType]: configOperators[operatorType],
      };
    }, {} as OperatorsMap);
  }, [signalRulesConfig]);

  const conditions = useMemo<SelectorValue[]>(() => {
    if (!signalRulesConfig) return [];

    const result = (Object.entries(signalRulesConfig.conditions) as [ConditionType, DataEntityBase[]][]).flatMap(([conditionType, configConditions]) => {
      return configConditions.flatMap((c) => {
        return mapCondition({
          condition: c,
          conditionType,
          operators: operators[c.uiType],
          queryOptions,
        });
      });
    });

    return result;
  }, [signalRulesConfig, queryOptions, operators]);

  const config = useMemo(
    () => ({
      conditions,
      timezone: selectedPartner?.timeZone,
      queryOptions,
    }),
    [conditions, selectedPartner?.timeZone, queryOptions],
  );

  if (!signalRulesConfig) {
    return DEFAULT_CONFIG;
  }

  return config;
}
